import { useMutation, useQueryClient } from '@tanstack/react-query'

import { postRequest } from 'src/lib/api-client'
import { logger } from 'src/lib/logger'
import { queries } from 'src/lib/query-keys'
import type { ConsumerAccountData, Consumer } from 'src/shared/features/consumers/schemas/consumer'

/**
 * Registers a consumer account in the backend after successful Cognito signup and confirmation
 * Authentication is handled via auth headers
 *
 * @param accountData Object containing consumer data
 * @returns The created consumer account
 */
export const createConsumerAccount = async (accountData: ConsumerAccountData): Promise<Consumer> => {
  try {
    const response = await postRequest<Consumer>('/consumers/account', accountData)
    if (!response) {
      throw new Error('No response received from consumer account registration')
    }
    return response
  } catch (error) {
    logger.error('Error registering consumer account in backend:', error)
    throw error
  }
}

/**
 * Hook for creating a consumer account after successful Cognito signup
 * To be used in conjunction with useConsumerConfirmSignUp
 * Authentication tokens are handled automatically via auth headers
 *
 * @returns Mutation function that takes consumer data
 */
export const useCreateConsumerAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createConsumerAccount,
    onSuccess: (response: Consumer) => {
      if (response) {
        queryClient.setQueryData(queries.consumers.detail(response.id).queryKey, response)
      }

      queryClient.invalidateQueries({ queryKey: queries.consumers.all.queryKey })
      queryClient.invalidateQueries({ queryKey: queries.auth.session.queryKey })
    },
  })
}
